/**
 * Delays the processing of the event until the user has stopped for a predetermined amount of time.
 * Reference: https://www.freecodecamp.org/news/javascript-debounce-example/
 */
export function debounce(func, timeout = 300) {
    let timer;

    return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => { func.apply(this, args); }, timeout);
    };
}

/**
 * Execute the scripts within an element.
 * Reference: https://stackoverflow.com/a/47614491/155899
 */
export function executeScripts(element) {
    element.querySelectorAll('script').forEach(script => {
        // Create the new script.
        const newScript = document.createElement('script');

        // Add the attributes.
        Array.from(script.attributes).forEach(a => newScript.setAttribute(a.name, a.value));

        // Set the inner HTML.
        newScript.appendChild(document.createTextNode(script.innerHTML));

        // Replace the old script with the new script tag.
        script.parentNode.replaceChild(newScript, script);
    });
}

/**
 * Overrides the default fetch method and feeds in some default options.
 */
export function fetch(url, options = {}) {
    const defaultOptions = {
        headers: {
            'X-Requested-With': 'XMLHttpRequest' // This is needed as by default the fetch API doesn't pass it and therefore there is no way to detect on the server whether the request is an ajax request.
        }
    };

    // Merge the options with the default options. Make sure the default headers are not overridden.
    if ('headers' in options)
        options.headers = { ...defaultOptions.headers, ...options.headers };
    else
        options = { ...defaultOptions, ...options };

    // Note: Automatically prefixing any locale request with the url prefix (if not already prefixed) makes sure the correct locale is set.
    return window.fetch(`${url.startsWith('/') && config.urlPrefix != null && !url.startsWith(`/${config.urlPrefix}/`) ? `/${config.urlPrefix}` : ''}${url}`, options);
}

/**
 * Gets the data object of an element from the prefix.
 * 
 * @param {bool} includeEmptyDataAttributes in razor when saying data-foo="null" it still renders an empty data attribute (unlike regular attributes which wouldn't render an attribute) so we may wish to ignore these.
 */
export function getData(element, prefix, includeEmptyDataAttributes = true) {
    // Get the elements data.
    const data = element.dataset;

    // Allow properties to be accessed sans prefix.
    for (const propertyName in data) {
        if (data.hasOwnProperty(propertyName) && (includeEmptyDataAttributes || data[propertyName]?.length > 0) && new RegExp('^' + prefix + '[A-Z]+').test(propertyName)) {
            const shortName = propertyName[prefix.length].toLowerCase() + propertyName.substr(prefix.length + 1);
            data[shortName] = data[propertyName];
        }
    }

    return data;
}

/**
 * Get the full height of an element (outer height + top/bottom margins).
 */
export function getHeight(element) {
    const style = getComputedStyle(element);
    return element.offsetHeight + parseInt(style.marginTop) + parseInt(style.marginBottom);
}

/**
 * Get the full width of an element (outer width + left/right margins).
 */
export function getWidth(element) {
    const style = getComputedStyle(element);
    return element.offsetWidth + parseInt(style.marginLeft) + parseInt(style.marginRight);
}

/**
 * Get the first and last name from a full name.
 */
export function getName(fullName) {
    fullName = fullName.trim();

    const firstName = fullName.includes(' ') ? fullName.substring(0, fullName.lastIndexOf(' ')) : fullName;
    const lastName = fullName.includes(' ') ? fullName.substring(fullName.lastIndexOf(' ') + 1) : null;

    return { firstName, lastName };
}

/**
 * Programatically load CSS.
 */
export function loadCss(config) {
    const head = document.getElementsByTagName('head')[0];

    if (config.url) {
        const link = document.createElement('link');
        link.href = config.url;
        link.rel = 'stylesheet';
        head.appendChild(link);
    } else {
        const style = document.createElement('style');
        style.innerHTML = config.content;
        head.appendChild(style);
    }
}

export function parseErrors(data, defaultErrorMessage = 'There was a problem processing your request.') {
    const errors = [];

    if (typeof data === 'object' && data !== null) {
        const values = Object.values(data).flat();

        for (var i = 0; i < values.length; i++) {
            errors.push(values[i]);
        }
    } else if (typeof data === 'string' && data != '')
        errors.push(data);
    else
        errors.push(defaultErrorMessage);

    return errors;
}

/**
 * Reference: https://simon-schraeder.de/posts/filereader-async/
 */
export function readFileAsDataURL(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = () => {
            resolve(reader.result);
        };

        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

/**
 * Inserts HTML to the element, execute scripts and fires the content updated event.
 */
export function setInnerHTML(element, html) {
    // Dispatch the "DOMContentUpdating" event against the element to update.
    const cancelled = !element.dispatchEvent(new Event('DOMContentUpdating', { bubbles: true, cancelable: true }));

    if (!cancelled) {
        // Set the inner HTML.
        element.innerHTML = html;

        // Execute the scripts.
        executeScripts(element);

        // Dispatch the "DOMContentUpdated" event against the updated element.
        element.dispatchEvent(new Event('DOMContentUpdated', { bubbles: true }));
    }
}